/**
 * The plan viewer's canvas: one zoomable stage showing a frame's drawing and
 * the annotations tagged on it.
 *
 * It draws through the **plan editor's own renderer** (`plan-editor/engine`),
 * not through a second implementation: same `SceneDoc`, same `renderWorld`,
 * same `--plan-*` palette from `plan-editor.css`, so the read-only view and the
 * editor are the same document seen twice. A viewer with its own colours and
 * its own idea of wall thickness makes a reader distrust both.
 *
 * Two coordinate systems meet here:
 *
 *   - **world** — the frame's units, which is what the engine draws in. For a
 *     representation no frame resolves (a room's own scan), the world is the
 *     crop's local frame instead: x ∈ [0,1], y ∈ [0, height].
 *   - **crop** — page-normalized [0,1]², which is what an annotation's geometry
 *     is stored in and what every handler below speaks. The `<g>` holding the
 *     annotations carries the crop → world transform, so shapes are written in
 *     crop units and the browser's own CTM does the conversion for pointer
 *     maths. Nothing here re-derives that by hand.
 *
 * Sizes that describe screen weight — stroke widths, handle radii, label text —
 * go through `px()`, which divides by the current zoom, so an annotation looks
 * the same at 1× and at 20×.
 */
import { plotStyleSheet, type ElementColour } from '../thematics/colour'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import {
    mdiVectorPolygon,
    mdiCircleOutline,
    mdiVectorLine,
    mdiCursorDefault,
    mdiMagnifyPlusOutline,
    mdiMagnifyMinusOutline,
    mdiFitToScreenOutline,
} from '@mdi/js'

import { apiBaseUrl } from '../../api/client'
import { Button } from '../ui/button'
import { useAsset } from '../../hooks/useQueries'
import { LAYER_IDS, defaultLayerStates, renderWorld, type LayerStates } from '../plan-editor/engine/render'
import { FIT_MARGIN, fitBox, fitScale, zoomRange } from '../plan-editor/engine/tools'
import { bboxOfPoints, clamp, IDENTITY, type BBox } from '../plan-editor/engine/geometry'
import { documentPoints, elementPoints, type SceneDoc } from '../plan-editor/engine/scene'
import { VECTOR_LAYERS, underlayState, type CompositePlanLayers } from './composite-plan-layers'
import '../plan-editor/plan-editor.css'
import type { components } from '../../api/schema'

type AssetRepresentation = components['schemas']['AssetRepresentation']
type Issue = components['schemas']['Issue'] & { properties?: Record<string, unknown> }
type AnnotationGeometry = components['schemas']['AnnotationGeometry']
type AnnotationProperties = components['schemas']['AnnotationProperties']

/**
 * Tags the shapes of the asset being viewed, so the hover highlight of a
 * clickable room (plan-editor.css) skips the one click that goes nowhere.
 */
function markViewed(html: string, uid?: string): string {
    if (!uid || !/^[0-9a-fA-F-]{8,40}$/.test(uid)) return html
    return html.replaceAll(`data-asset-uid="${uid}"`, `data-asset-uid="${uid}" data-viewed="true"`)
}

export interface AnnotationDraft {
    geometry: AnnotationGeometry
    page: number
}

interface Props {
    org: string
    representation: AssetRepresentation
    annotations: Issue[]
    onAnnotationClick: (iss: Issue) => void
    onAnnotationDraft: (draft: AnnotationDraft) => void
    /** Annotation currently being edited / created — drawn as a translucent preview overlay. */
    draft?: AnnotationDraft | null
    /** Optional: when set, vertex handles for the issue's geometry are rendered as drag targets. */
    editingIssueUid?: string | null
    editingGeometry?: AnnotationGeometry | null
    onEditingGeometryChange?: (g: AnnotationGeometry) => void
    /**
     * The frame's document, when the server resolves this crop as one surface
     * of a frame: the scan *and* everything drawn on it, positioned. Absent for
     * a representation that stands alone, which is then drawn on its own.
     */
    scene?: SceneDoc | null
    /** Whether the drawn geometry is painted over the scan (the scan always is). */
    showGeometry?: boolean
    /**
     * The reader's layer choices (patterns §45): which vector layers draw,
     * whether the raster sheets draw at all, and each sheet's own visibility
     * and opacity, keyed by registration uid, else representation uid.
     * Unset draws everything, as before.
     */
    layerVisibility?: CompositePlanLayers
    /** Localizes an asset type code, so an unnamed room reads as "Chambre 2". */
    typeLabelOf?: (code: string) => string
    /**
     * Follow a drawn space to the asset it stands for. When set, the spaces
     * layer becomes hit-testable in `select` mode and a click on a room hands
     * back its asset uid — the plan is then the navigation surface it looks
     * like, instead of a picture you have to match against the children list.
     * Unset leaves the whole drawing inert, as it was.
     */
    onSceneAssetClick?: (assetUid: string) => void
    /**
     * The asset the view is ABOUT, when the scene was built from an ancestor's
     * frame (spec 2026-09-17-01 §3). The initial fit is then computed from that
     * asset's own geometry — its elements and its children's — instead of the
     * whole scene, so opening one lot does not land the reader on the entire
     * floor. Unset (and every editor document) fits the scene, as before.
     */
    subjectAssetUid?: string
    /** The asset whose page shows this plan: its own shapes are no-op clicks, so they get no hover highlight. */
    viewedAssetUid?: string
    /**
     * Every asset under the subject. A bundle owned by the floor depicts the
     * lot's rooms by uid alone, with no parent, so this is what places them
     * under the lot.
     */
    subjectDescendantUids?: ReadonlySet<string>
    /**
     * Whether this canvas may CREATE annotations. False on a composite whose
     * base raster belongs to another asset's frame (spec 2026-09-17-01): an
     * annotation is stored against a representation, so one drawn there would
     * be filed on the floor's scan and then read back on the floor's Plan tab
     * and on every sibling lot's, with nothing recording which asset it was
     * about. Existing annotations still READ — they belong to the drawing on
     * screen, and hiding them would make two pages disagree about what is
     * marked on the same scan.
     */
    annotatable?: boolean
    /**
     * Thematic colours ("Colour by"), element asset uid -> colour. The drawn
     * spaces and equipment are painted from it by `data-asset-uid`.
     */
    colourIndex?: Map<string, ElementColour>
    /** The thematic class isolated in the legend; the other elements dim. */
    isolated?: string | null
}

type Tool = 'select' | 'polygon' | 'point' | 'line'

interface View {
    k: number
    x: number
    y: number
}

const PALETTE = '#4A90D9'

/** A drag under this many pixels is a click, not a pan. */
const PAN_SLOP = 3

function authedRenderUrl(org: string, repUid: string, kind: string, dpi = 150): string {
    let token: string | null = null
    try {
        token = localStorage.getItem('token')
    } catch {
        // localStorage may be unavailable in some sandboxed contexts.
    }
    const sep = token ? `&token=${encodeURIComponent(token)}` : ''
    // kind=vector only renders to SVG — the backend refuses raster formats for it.
    const params = kind === 'vector' ? 'format=svg' : `dpi=${dpi}&format=png`
    return `${apiBaseUrl}/api/v1/organizations/${org}/representations/${repUid}/render?${params}${sep}`
}

/** The layers whose drawn assets are links in the viewer: a room, a lot, a piece of equipment. */
const LINK_LAYERS: ReadonlySet<string> = new Set(['spaces', 'equipment'])

/** The layer states the viewer draws with: the editor's, minus what is hidden. */
function viewerLayers(showGeometry: boolean, visibility?: CompositePlanLayers): LayerStates {
    const layers = defaultLayerStates()
    if (visibility) {
        layers.underlays = { ...layers.underlays, on: visibility.underlaysOn }
        for (const id of VECTOR_LAYERS) {
            if (!visibility.vector[id]) layers[id] = { ...layers[id], on: false }
        }
    }
    if (showGeometry) return layers
    for (const id of LAYER_IDS) {
        if (id !== 'underlays') layers[id] = { ...layers[id], on: false }
    }
    return layers
}

export function PlanCanvas({
    org,
    representation,
    annotations,
    onAnnotationClick,
    onAnnotationDraft,
    draft,
    editingIssueUid,
    editingGeometry,
    onEditingGeometryChange,
    scene,
    showGeometry = true,
    layerVisibility,
    typeLabelOf,
    onSceneAssetClick,
    subjectAssetUid,
    viewedAssetUid,
    subjectDescendantUids,
    annotatable = true,
    colourIndex,
    isolated = null,
}: Props) {
    const { t } = useTranslation('assets')
    const svgRef = useRef<SVGSVGElement>(null)
    const annoRef = useRef<SVGGElement>(null)
    const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null)
    const [imgError, setImgError] = useState(false)
    const [tool, setTool] = useState<Tool>('select')
    // drawingPoints are in CROP-LOCAL [0,1] (the annotation group's units).
    const [drawingPoints, setDrawingPoints] = useState<{ x: number; y: number }[]>([])
    const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null)
    const [hoveredUid, setHoveredUid] = useState<string | null>(null)
    const [draggingHandleIndex, setDraggingHandleIndex] = useState<number | null>(null)
    const [view, setView] = useState<View>({ k: 1, x: 0, y: 0 })
    // The scale of the last fit: the zoom percentage reads 100% there.
    const [fitK, setFitK] = useState(1)
    // Set once the reader moves the view, so a resize stops re-fitting under them.
    const movedRef = useRef(false)
    const panRef = useRef<{ x: number; y: number; view: View; moved: boolean } | null>(null)
    const suppressClickRef = useRef(false)

    const url = useMemo(
        () => authedRenderUrl(org, representation.uid, representation.kind),
        [org, representation.uid, representation.kind],
    )

    const value = (representation.value as { sheets?: { page?: number; bbox?: { x: number; y: number; w: number; h: number } }[] } | undefined)
    const sheet = value?.sheets?.[0]
    const bbox = useMemo(() => sheet?.bbox ?? { x: 0, y: 0, w: 1, h: 1 }, [sheet?.bbox])
    const page = sheet?.page ?? 1

    // Where this crop sits in the world. Inside a resolved frame the scene's own
    // underlay entry says so — the same row the engine draws the raster from, so
    // the annotations and the scan cannot drift apart. Standing alone, the crop
    // *is* the world and only its aspect matters.
    const baseUnderlay = useMemo(
        () => scene?.underlays.find(u => u.repUid === representation.uid) ?? null,
        [scene, representation.uid],
    )
    const cropTransform = baseUnderlay?.transform ?? IDENTITY
    const cropHeight = baseUnderlay?.height
        ?? (imgSize && imgSize.w > 0 ? imgSize.h / imgSize.w : null)

    // The raster is drawn by the engine when the scene carries it; alone, this
    // canvas draws it itself — and loading it through an off-DOM Image is what
    // tells us the crop's aspect, which the whole layout hangs on.
    //
    // A scene with no raster base at all — a lot's interior, composed from the
    // geometry resolved into its own frame (spec 2026-09-23-02) — has nothing
    // to load: the world is the scene, and the fit reads the scene's extent.
    const drawsOwnRaster = !baseUnderlay && !(scene && representation.kind === 'vector')
    useEffect(() => {
        setImgError(false)
        setImgSize(null)
        if (!drawsOwnRaster) return
        const img = new Image()
        let cancelled = false
        img.onload = () => {
            if (!cancelled) setImgSize({ w: img.naturalWidth, h: img.naturalHeight })
        }
        img.onerror = () => {
            if (!cancelled) setImgError(true)
        }
        img.src = url
        return () => {
            cancelled = true
        }
    }, [url, drawsOwnRaster])

    // Page-normalized -> crop-relative (in [0,1] crop coords).
    const projectPoint = useCallback(
        (pt: { x: number; y: number }) => ({
            x: (pt.x - bbox.x) / bbox.w,
            y: (pt.y - bbox.y) / bbox.h,
        }),
        [bbox],
    )

    // Inverse: crop-normalized -> page-normalized.
    const invProject = useCallback(
        (pt: { x: number; y: number }) => ({
            x: bbox.x + pt.x * bbox.w,
            y: bbox.y + pt.y * bbox.h,
        }),
        [bbox],
    )

    /* -------------------------------------------------------------- the view */

    // Crop -> world, in the engine's own terms: place the crop's unit square by
    // its registration, after stretching y to the crop's aspect.
    const cropToWorld = useMemo(() => {
        const h = cropHeight ?? 1
        const tr = cropTransform
        return `translate(${tr.tx} ${tr.ty}) rotate(${tr.rotation_deg}) scale(${tr.scale}) scale(1 ${h})`
    }, [cropTransform, cropHeight])

    /**
     * The subject's own shape, when the scene was borrowed from an ancestor's
     * frame (spec 2026-09-17-01 §3): what the reader asked to look at, inside a
     * page that is mostly not theirs. Its children count too — a lot whose
     * outline was never drawn is still located by the rooms inside it — and no
     * match at all falls back to the scene, which is better than an unframed
     * view.
     */
    const subjectBBox = useMemo<BBox | null>(() => {
        if (!scene || !subjectAssetUid) return null
        const mine = scene.elements.filter(el =>
            el.owner.assetUid === subjectAssetUid || el.owner.assetParentUid === subjectAssetUid
            || (!!el.owner.assetUid && !!subjectDescendantUids?.has(el.owner.assetUid)))
        return mine.length ? bboxOfPoints(mine.flatMap((el) => elementPoints(el))) : null
    }, [scene, subjectAssetUid, subjectDescendantUids])

    const contentBBox = useMemo<BBox | null>(() => {
        if (subjectBBox) return subjectBBox
        if (scene) return bboxOfPoints(documentPoints(scene))
        if (cropHeight == null) return null
        return { x0: 0, y0: 0, x1: 1, y1: cropHeight }
    }, [scene, cropHeight, subjectBBox])

    const zoomLimits = useCallback(() => {
        const r = svgRef.current?.getBoundingClientRect()
        if (!r || r.width < 40 || r.height < 40) return zoomRange(view.k)
        return zoomRange(fitScale(r, contentBBox))
    }, [contentBBox, view.k])

    const fitView = useCallback(() => {
        const r = svgRef.current?.getBoundingClientRect()
        if (!r || r.width < 40 || r.height < 40 || !contentBBox) return
        // A subject box is a REGION of the document, which `fitScale`'s
        // one-world-unit floor swallows whole on an uncalibrated frame; see
        // `fitBox`. Everything else takes the editor's shared maths unchanged.
        const { k: raw, w, h } = fitBox(r, contentBBox, contentBBox === subjectBBox)
        // The reachable range is still measured against the ordinary fit, and
        // clamping into it is a no-op for every non-tight view (`FIT_MARGIN` is
        // well inside it). It is the last guard against a degenerate box
        // producing a scale nothing can zoom back out of.
        const { min, max } = zoomRange(fitScale(r, contentBBox))
        const k = clamp(raw * FIT_MARGIN, min, max)
        setFitK(k)
        setView({
            k,
            x: (r.width - w * k) / 2 - contentBBox.x0 * k,
            y: (r.height - h * k) / 2 - contentBBox.y0 * k,
        })
    }, [contentBBox, subjectBBox])

    // Fit whenever the subject changes: another representation, a scene that
    // just arrived, an aspect ratio that just loaded.
    useLayoutEffect(() => {
        movedRef.current = false
        fitView()
    }, [fitView, representation.uid])

    // …and while nobody has moved the view, keep it fitted across resizes (a
    // collapsing sidebar, a rotated phone).
    useEffect(() => {
        const el = svgRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver(() => {
            if (!movedRef.current) fitView()
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [fitView])

    const zoomAt = useCallback((screen: { x: number; y: number }, factor: number) => {
        const { min, max } = zoomLimits()
        movedRef.current = true
        setView(v => {
            const k = clamp(v.k * factor, min, max)
            const f = k / v.k
            return { k, x: screen.x - (screen.x - v.x) * f, y: screen.y - (screen.y - v.y) * f }
        })
    }, [zoomLimits])

    const zoomByButton = useCallback((factor: number) => {
        const r = svgRef.current?.getBoundingClientRect()
        zoomAt({ x: (r?.width ?? 0) / 2, y: (r?.height ?? 0) / 2 }, factor)
    }, [zoomAt])

    // Wheel has to be a non-passive listener, or the page scrolls under the plan.
    useEffect(() => {
        const el = svgRef.current
        if (!el) return
        const onWheel = (e: WheelEvent) => {
            e.preventDefault()
            const r = el.getBoundingClientRect()
            zoomAt({ x: e.clientX - r.left, y: e.clientY - r.top }, Math.exp(-e.deltaY * 0.0015))
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [zoomAt])

    /* -------------------------------------------------------- pointer -> crop */

    // Straight through the browser's own CTM: the annotation group already
    // carries the view *and* the crop -> world transform, so this stays correct
    // however the plan is zoomed, panned or registered.
    const cropPointFromEvent = useCallback((e: { clientX: number; clientY: number }) => {
        const g = annoRef.current
        const svg = svgRef.current
        if (!g || !svg) return { x: 0, y: 0 }
        const ctm = g.getScreenCTM()
        if (!ctm) return { x: 0, y: 0 }
        const p = svg.createSVGPoint()
        p.x = e.clientX
        p.y = e.clientY
        const q = p.matrixTransform(ctm.inverse())
        return { x: q.x, y: q.y }
    }, [])

    /** One screen pixel, in crop units — the annotation layer's own scale. */
    const cropPixel = 1 / Math.max(view.k * cropTransform.scale, 1e-9)
    const px = useCallback((n: number) => n * cropPixel, [cropPixel])

    /* ---------------------------------------------------------------- panning */

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        // The middle button pans whatever the tool; the left button only does
        // when no drawing tool wants the drag.
        if (e.button !== 1 && !(e.button === 0 && tool === 'select')) return
        panRef.current = { x: e.clientX, y: e.clientY, view, moved: false }
        svgRef.current?.setPointerCapture(e.pointerId)
    }

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        setCursor(cropPointFromEvent(e))
        const pan = panRef.current
        if (!pan) return
        const dx = e.clientX - pan.x
        const dy = e.clientY - pan.y
        if (!pan.moved && Math.hypot(dx, dy) < PAN_SLOP) return
        pan.moved = true
        movedRef.current = true
        setView({ k: pan.view.k, x: pan.view.x + dx, y: pan.view.y + dy })
    }

    const endPan = (e: React.PointerEvent<SVGSVGElement>) => {
        const pan = panRef.current
        if (!pan) return
        svgRef.current?.releasePointerCapture(e.pointerId)
        panRef.current = null
        // A drag that moved must not also place a drawing point: the click event
        // this pointer-up produces is swallowed by the flag below.
        if (pan.moved) suppressClickRef.current = true
    }

    /* ---------------------------------------------------------------- drawing */

    // Switching to a canvas that may not be annotated disarms whatever tool was
    // left armed on the previous one. The toolbar is not rendered there, so
    // nothing can re-arm it, and `select` is the only state that reaches no
    // `onAnnotationDraft` call at all.
    useEffect(() => {
        if (annotatable) return
        setTool('select')
        setDrawingPoints([])
    }, [annotatable])

    const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false
            return
        }
        if (tool === 'select') {
            // Following a room to its asset. Only in `select` — while a drawing
            // tool is armed the click is the drawing's, and a pan has already
            // been swallowed by suppressClickRef above. A space the scene drew
            // but that has no asset yet (an unsaved `newAsset`) carries no uid
            // and is simply not a link.
            if (spacesAreLinks) {
                // Resolved from the CURSOR, never from `e.target`. Panning calls
                // setPointerCapture on this svg, and a captured pointer retargets
                // every later event in the sequence — the click included — to the
                // capturing element. So `e.target` is ALWAYS this svg here, and
                // reading the room off it silently never matches: the shape is
                // painted, hit-testable and hovering correctly, and the click
                // still does nothing.
                const under = document.elementFromPoint(e.clientX, e.clientY)
                const uid = under?.closest?.('[data-asset-uid]')?.getAttribute('data-asset-uid')
                if (uid) onSceneAssetClick?.(uid)
            }
            return
        }
        const cropPt = cropPointFromEvent(e)
        if (tool === 'point') {
            const pagePt = invProject(cropPt)
            onAnnotationDraft({
                page,
                geometry: { kind: 'point', points: [pagePt] },
            })
            setTool('select')
            setDrawingPoints([])
            return
        }
        if (tool === 'polygon') {
            setDrawingPoints(prev => [...prev, cropPt])
        } else if (tool === 'line') {
            const next = [...drawingPoints, cropPt]
            if (next.length === 2) {
                onAnnotationDraft({
                    page,
                    geometry: { kind: 'line', points: next.map(invProject) },
                })
                setTool('select')
                setDrawingPoints([])
            } else {
                setDrawingPoints(next)
            }
        }
    }

    const handleSvgDoubleClick = () => {
        if (tool === 'polygon' && drawingPoints.length >= 3) {
            onAnnotationDraft({
                page,
                geometry: { kind: 'polygon', points: drawingPoints.map(invProject) },
            })
            setTool('select')
            setDrawingPoints([])
        }
    }

    const handleSvgLeave = () => setCursor(null)

    const cancelDrawing = useCallback(() => {
        setTool('select')
        setDrawingPoints([])
    }, [])

    // Keyboard: Esc cancels drawing, Enter finishes polygon.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                cancelDrawing()
            } else if (e.key === 'Enter' && tool === 'polygon' && drawingPoints.length >= 3) {
                onAnnotationDraft({
                    page,
                    geometry: { kind: 'polygon', points: drawingPoints.map(invProject) },
                })
                setTool('select')
                setDrawingPoints([])
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [tool, drawingPoints, invProject, onAnnotationDraft, page, cancelDrawing])

    // Vertex-edit drag handlers (window-level so a fast drag doesn't lose track
    // when the cursor leaves the SVG bounds).
    useEffect(() => {
        if (draggingHandleIndex === null || !editingGeometry || !onEditingGeometryChange) return
        const onMove = (e: MouseEvent) => {
            const cropPt = cropPointFromEvent(e)
            const pagePt = invProject(cropPt)
            const points = editingGeometry.points.map((p, i) =>
                i === draggingHandleIndex ? pagePt : p,
            )
            onEditingGeometryChange({ ...editingGeometry, points })
        }
        const onUp = () => setDraggingHandleIndex(null)
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
        return () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
    }, [draggingHandleIndex, editingGeometry, onEditingGeometryChange, cropPointFromEvent, invProject])

    /* -------------------------------------------------------------- the world */

    const layers = useMemo(() => viewerLayers(showGeometry, layerVisibility), [showGeometry, layerVisibility])
    // Each sheet's own visibility and opacity ride on the scene's underlay
    // entries, which is what the engine reads. A copy: the scene is shared
    // with everything else on the page.
    const drawnScene = useMemo(() => {
        if (!scene || !layerVisibility || Object.keys(layerVisibility.underlays).length === 0) return scene
        return {
            ...scene,
            underlays: scene.underlays.map((u) => {
                const state = underlayState(layerVisibility, u.registrationUid ?? u.repUid)
                return { ...u, visible: u.visible && state.visible, opacity: u.opacity * state.opacity }
            }),
        }
    }, [scene, layerVisibility])
    const hiddenNetworks = useMemo(
        () => (layerVisibility?.hiddenNetworks.length ? new Set(layerVisibility.hiddenNetworks) : undefined),
        [layerVisibility],
    )
    const world = useMemo(() => {
        if (!drawnScene) return null
        return renderWorld(drawnScene, {
            layers,
            scale: view.k,
            ...(hiddenNetworks ? { hiddenNetworks } : {}),
            underlayHref: repUid => authedRenderUrl(org, repUid, 'pdf'),
            ...(typeLabelOf ? { typeLabelOf } : {}),
        })
    }, [drawnScene, layers, view.k, org, typeLabelOf, hiddenNetworks])

    /**
     * Whether a drawn room currently acts as a link to its asset.
     *
     * Gated on the `select` tool (a drawing tool's click belongs to the
     * drawing) and off while vertex handles are being dragged, so reshaping an
     * annotation over a room cannot navigate away mid-edit.
     */
    const spacesAreLinks = Boolean(onSceneAssetClick) && tool === 'select' && !editingIssueUid && world != null

    /* --------------------------------------------------------- annotation ink */

    const renderGeometry = useCallback(
        (g: AnnotationGeometry, color: string, opts: { dashed?: boolean; opacity?: number; key?: string; testId?: string; onClick?: (e: React.MouseEvent) => void; onMouseEnter?: () => void; onMouseLeave?: () => void; emphasize?: boolean }) => {
            const { dashed, opacity = 0.2, key, testId, onClick, onMouseEnter, onMouseLeave, emphasize } = opts
            const projected = g.points.map(projectPoint)
            const allOut = projected.every(p => p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1)
            if (allOut && !dashed) return null
            const baseProps = {
                onClick,
                onMouseEnter,
                onMouseLeave,
                'data-testid': testId,
                className: onClick ? 'cursor-pointer' : '',
            } as const
            const stroke = color
            const strokeDasharray = dashed ? `${px(4)} ${px(4)}` : undefined
            switch (g.kind) {
                case 'polygon':
                    // Non-scaling strokes are in screen pixels, so the width stays
                    // equal on both axes under the non-uniform crop -> world scale.
                    // A white halo under the colour keeps it apart from room fills.
                    return (
                        <g key={key} {...baseProps}>
                            <polygon
                                points={projected.map(p => `${p.x},${p.y}`).join(' ')}
                                fill="none"
                                stroke="white"
                                strokeWidth={emphasize ? 7 : 5.5}
                                vectorEffect="non-scaling-stroke"
                            />
                            <polygon
                                points={projected.map(p => `${p.x},${p.y}`).join(' ')}
                                fill={color}
                                fillOpacity={emphasize ? 0.4 : opacity}
                                stroke={stroke}
                                strokeWidth={emphasize ? 4 : 2.5}
                                strokeDasharray={dashed ? '6 4' : '8 4'}
                                vectorEffect="non-scaling-stroke"
                            />
                        </g>
                    )
                case 'point': {
                    // A pin drawn in a screen-sized frame: the inner scale/rotate
                    // cancel the crop's y stretch and rotation, so it stays round.
                    const unstretch = `scale(1 ${1 / (cropHeight ?? 1)}) rotate(${-cropTransform.rotation_deg})`
                    return (
                        <g
                            key={key}
                            transform={`translate(${projected[0].x} ${projected[0].y}) ${unstretch} scale(${px(emphasize ? 1.25 : 1)})`}
                            data-pin="true"
                            {...baseProps}
                        >
                            <path
                                d="M0 0 C-3 -8 -9 -12 -9 -18 A9 9 0 1 1 9 -18 C9 -12 3 -8 0 0 Z"
                                fill={color}
                                stroke="white"
                                strokeWidth={1.6}
                                strokeDasharray={dashed ? '3 2' : undefined}
                            />
                            <circle cx={0} cy={-18} r={3.5} fill="white" />
                        </g>
                    )
                }
                case 'line':
                    return (
                        <line
                            key={key}
                            x1={projected[0].x}
                            y1={projected[0].y}
                            x2={projected[1].x}
                            y2={projected[1].y}
                            stroke={stroke}
                            strokeWidth={emphasize ? px(5) : px(3.5)}
                            strokeDasharray={strokeDasharray}
                            {...baseProps}
                        />
                    )
                case 'arrow':
                    return (
                        <line
                            key={key}
                            x1={projected[0].x}
                            y1={projected[0].y}
                            x2={projected[1].x}
                            y2={projected[1].y}
                            stroke={stroke}
                            strokeWidth={emphasize ? px(5) : px(3.5)}
                            markerEnd="url(#plan-arrow)"
                            strokeDasharray={strokeDasharray}
                            {...baseProps}
                        />
                    )
                case 'text':
                    // Read-only legacy geometry — keep the existing render path
                    // for backward compatibility; the toolbar no longer creates
                    // new text annotations.
                    return (
                        <g key={key} {...baseProps}>
                            <text
                                x={projected[0].x}
                                y={projected[0].y}
                                fontSize={px(16)}
                                fill={color}
                                stroke="white"
                                strokeWidth={px(1)}
                                paintOrder="stroke"
                            >
                                ·
                            </text>
                        </g>
                    )
                default:
                    return null
            }
        },
        [projectPoint, px, cropHeight, cropTransform.rotation_deg],
    )

    const tooltipForHovered = useMemo(() => {
        if (!hoveredUid) return null
        const iss = annotations.find(a => a.uid === hoveredUid)
        if (!iss) return null
        const props = (iss.properties as { annotation?: AnnotationProperties } | undefined)?.annotation
        const geom = props?.geometry
        if (!geom) return null
        const proj = geom.points.map(projectPoint)
        const cx = proj.reduce((s, p) => s + p.x, 0) / proj.length
        const cy = proj.reduce((s, p) => s + p.y, 0) / proj.length
        return { uid: hoveredUid, title: iss.title || '', cx, cy, subjectUid: props?.subject_asset_uid ?? '' }
    }, [hoveredUid, annotations, projectPoint])
    // Only the hovered annotation's linked asset is fetched (cached by react-query).
    const { data: hoveredSubject } = useAsset(org, tooltipForHovered?.subjectUid ?? '')
    const tooltipText = tooltipForHovered
        ? [tooltipForHovered.title, hoveredSubject?.name].filter(Boolean).join(' · ')
        : ''

    const zoomInLabel = t('plan.zoom_in', 'Zoom in')
    const zoomOutLabel = t('plan.zoom_out', 'Zoom out')
    const fitLabel = t('plan.zoom_fit', 'Fit to view')
    const zoomLevelLabel = t('plan.zoom_level', 'Zoom level')

    return (
        <div className="space-y-2" data-testid="plan-canvas">
            <div className="flex flex-wrap items-center gap-1">
                {annotatable && <DrawToolbar tool={tool} onToolChange={setTool} onCancel={cancelDrawing} />}
                <div className="ml-auto flex items-center gap-1">
                    <Button variant="outline" size="icon" aria-label={zoomOutLabel} title={zoomOutLabel} onClick={() => zoomByButton(1 / 1.3)} data-testid="plan-zoom-out">
                        <AppIcon path={mdiMagnifyMinusOutline} size="sm" />
                    </Button>
                    <span
                        className="min-w-12 text-center text-xs tabular-nums text-muted-foreground"
                        aria-label={zoomLevelLabel}
                        title={zoomLevelLabel}
                        data-testid="plan-zoom-level"
                    >
                        {Math.round((view.k / fitK) * 100)}%
                    </span>
                    <Button variant="outline" size="icon" aria-label={zoomInLabel} title={zoomInLabel} onClick={() => zoomByButton(1.3)} data-testid="plan-zoom-in">
                        <AppIcon path={mdiMagnifyPlusOutline} size="sm" />
                    </Button>
                    <Button variant="outline" size="icon" aria-label={fitLabel} title={fitLabel} onClick={fitView} data-testid="plan-zoom-fit">
                        <AppIcon path={mdiFitToScreenOutline} size="sm" />
                    </Button>
                </div>
            </div>

            <div className="relative overflow-hidden rounded-md border border-border" data-testid="plan-canvas-substrate">
                {imgError ? (
                    <div className="p-12 text-center text-muted-foreground" data-testid="plan-render-error">
                        {t('plan.render_error', 'Could not render this plan. The source document may still be processing.')}
                    </div>
                ) : (
                    <svg
                        ref={svgRef}
                        className={`plan-editor-stage h-[70vh] max-h-[820px] min-h-[360px] w-full ${tool === 'select' ? 'cursor-grab' : 'cursor-crosshair'}`}
                        onClick={handleSvgClick}
                        onDoubleClick={handleSvgDoubleClick}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endPan}
                        onPointerCancel={endPan}
                        onMouseLeave={handleSvgLeave}
                        data-testid="plan-svg-overlay"
                    >
                        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`} data-testid="plan-world">
                            {/* The drawing, through the plan editor's renderer,
                                raster underlays included. It is a picture, not a
                                target: every pointer event belongs to the
                                annotation layer above it. */}
                            {/* The drawing is a picture, not a target — except for the
                                spaces layer once a caller wants its rooms followed. The
                                engine already stamps `data-asset-uid` on every space it
                                draws, so making the spaces and equipment layers
                                hit-testable is the whole mechanism. Equipment is painted
                                above the rooms and is small, so it takes the click only
                                where it is drawn; walls, runs and labels stay inert and
                                cannot swallow a click meant for the room under them.

                                The `none` lives on each layer rather than on this wrapper:
                                a `pointer-events: none` ANCESTOR prunes the whole subtree
                                from real hit-testing in Chromium, so a descendant that
                                re-enables itself still never receives the click — it only
                                looks reachable, `document.elementFromPoint` happily
                                returning the shape that events never reach. */}
                            {world && colourIndex && colourIndex.size > 0 && (
                                <style data-testid="plan-thematic-style">{plotStyleSheet(colourIndex, isolated)}</style>
                            )}
                            {world && (
                                <g
                                    style={spacesAreLinks ? undefined : { pointerEvents: 'none' }}
                                    data-testid="plan-vector-overlay"
                                >
                                    {LAYER_IDS.map(id => (
                                        <g
                                            key={id}
                                            className={`plan-layer-${id}${spacesAreLinks && LINK_LAYERS.has(id) ? ` plan-layer-${id}-clickable` : ''}`}
                                            style={spacesAreLinks ? { pointerEvents: LINK_LAYERS.has(id) ? 'auto' : 'none' } : undefined}
                                            dangerouslySetInnerHTML={{ __html: id === 'spaces' ? markViewed(world[id], viewedAssetUid) : world[id] }}
                                        />
                                    ))}
                                </g>
                            )}

                            {/* A representation no frame resolves draws its own
                                crop — what the engine would emit for an underlay,
                                minus a scene to hang it on. */}
                            {!world && cropHeight != null && (
                                <image
                                    href={url}
                                    x={0}
                                    y={0}
                                    width={1}
                                    height={cropHeight}
                                    preserveAspectRatio="none"
                                    style={{ pointerEvents: 'none' }}
                                    data-testid="plan-image"
                                />
                            )}

                            <g ref={annoRef} transform={cropToWorld} data-testid="plan-annotation-layer">
                                {/* Saved annotations */}
                                {annotations.map(iss => {
                                    // Skip the one currently being geometry-edited; we render its
                                    // editable copy below.
                                    if (editingIssueUid === iss.uid && editingGeometry) return null
                                    const props = (iss.properties as { annotation?: AnnotationProperties } | undefined)?.annotation
                                    if (!props) return null
                                    if (props.page !== page) return null
                                    const color = props.style?.color || PALETTE
                                    const onClick = (e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        onAnnotationClick(iss)
                                    }
                                    return renderGeometry(props.geometry, color, {
                                        key: iss.uid,
                                        testId: `plan-annotation-${iss.uid}`,
                                        onClick,
                                        onMouseEnter: () => setHoveredUid(iss.uid),
                                        onMouseLeave: () => setHoveredUid(prev => (prev === iss.uid ? null : prev)),
                                        emphasize: hoveredUid === iss.uid,
                                    })
                                })}

                                {/* Edit-geometry preview — same color as saved, but with dashed stroke */}
                                {editingIssueUid && editingGeometry && (() => {
                                    const iss = annotations.find(a => a.uid === editingIssueUid)
                                    const props = iss
                                        ? (iss.properties as { annotation?: AnnotationProperties } | undefined)?.annotation
                                        : undefined
                                    const color = props?.style?.color || PALETTE
                                    return renderGeometry(editingGeometry, color, {
                                        key: 'editing-geom',
                                        testId: 'plan-editing-geometry',
                                        dashed: true,
                                        opacity: 0.25,
                                    })
                                })()}

                                {/* Vertex handles for edit mode */}
                                {editingIssueUid && editingGeometry && editingGeometry.points.map((p, i) => {
                                    const proj = projectPoint(p)
                                    return (
                                        <circle
                                            key={`handle-${i}`}
                                            cx={proj.x}
                                            cy={proj.y}
                                            r={px(10)}
                                            fill="white"
                                            stroke={PALETTE}
                                            strokeWidth={px(2.5)}
                                            className="cursor-grab"
                                            onPointerDown={e => e.stopPropagation()}
                                            onMouseDown={e => {
                                                e.stopPropagation()
                                                setDraggingHandleIndex(i)
                                            }}
                                            data-testid={`plan-edit-handle-${i}`}
                                        />
                                    )
                                })}

                                {/* Draft preview (annotation panel is open with a pending shape) */}
                                {draft && renderGeometry(draft.geometry, PALETTE, {
                                    key: 'draft-preview',
                                    testId: 'plan-draft-preview',
                                    opacity: 0.3,
                                    dashed: false,
                                })}

                                {/* In-progress drawing — dashed rubber band to cursor */}
                                {tool === 'polygon' && drawingPoints.length > 0 && (
                                    <>
                                        {drawingPoints.length >= 3 && (
                                            <polygon
                                                points={drawingPoints.map(p => `${p.x},${p.y}`).join(' ')}
                                                fill={PALETTE}
                                                fillOpacity={0.15}
                                                stroke={PALETTE}
                                                strokeDasharray={`${px(4)} ${px(4)}`}
                                                strokeWidth={px(2.5)}
                                            />
                                        )}
                                        {drawingPoints.length === 2 && (
                                            <line
                                                x1={drawingPoints[0].x}
                                                y1={drawingPoints[0].y}
                                                x2={drawingPoints[1].x}
                                                y2={drawingPoints[1].y}
                                                stroke={PALETTE}
                                                strokeWidth={px(2.5)}
                                                strokeDasharray={`${px(4)} ${px(4)}`}
                                            />
                                        )}
                                        {drawingPoints.map((p, i) => (
                                            <circle key={`pp-${i}`} cx={p.x} cy={p.y} r={px(4)} fill={PALETTE} />
                                        ))}
                                        {cursor && (
                                            <line
                                                x1={drawingPoints[drawingPoints.length - 1].x}
                                                y1={drawingPoints[drawingPoints.length - 1].y}
                                                x2={cursor.x}
                                                y2={cursor.y}
                                                stroke={PALETTE}
                                                strokeWidth={px(2.5)}
                                                strokeDasharray={`${px(4)} ${px(4)}`}
                                                opacity={0.7}
                                            />
                                        )}
                                    </>
                                )}
                                {tool === 'line' && drawingPoints.length === 1 && (
                                    <>
                                        <circle cx={drawingPoints[0].x} cy={drawingPoints[0].y} r={px(4)} fill={PALETTE} />
                                        {cursor && (
                                            <line
                                                x1={drawingPoints[0].x}
                                                y1={drawingPoints[0].y}
                                                x2={cursor.x}
                                                y2={cursor.y}
                                                stroke={PALETTE}
                                                strokeWidth={px(2.5)}
                                                strokeDasharray={`${px(4)} ${px(4)}`}
                                                opacity={0.7}
                                            />
                                        )}
                                    </>
                                )}
                                {tool === 'point' && cursor && (
                                    <circle cx={cursor.x} cy={cursor.y} r={px(7)} fill={PALETTE} fillOpacity={0.4} stroke={PALETTE} strokeWidth={px(1.6)} />
                                )}

                                {/* Hover tooltip */}
                                {tooltipForHovered && tooltipText && (
                                    <g
                                        pointerEvents="none"
                                        data-testid="plan-annotation-tooltip"
                                        transform={`translate(${tooltipForHovered.cx} ${tooltipForHovered.cy}) scale(1 ${1 / (cropHeight ?? 1)}) rotate(${-cropTransform.rotation_deg}) scale(${px(1)})`}
                                    >
                                        <rect x={0} y={-58} width={190} height={26} fill="white" fillOpacity={0.92} stroke={PALETTE} strokeWidth={1.6} rx={4} />
                                        <text x={6} y={-40} fontSize={13} fill="black">
                                            {tooltipText.length > 28 ? `${tooltipText.slice(0, 28)}…` : tooltipText}
                                        </text>
                                    </g>
                                )}

                                {/* Arrow marker def */}
                                <defs>
                                    <marker
                                        id="plan-arrow"
                                        viewBox="0 0 10 10"
                                        refX="10"
                                        refY="5"
                                        markerWidth="3"
                                        markerHeight="3"
                                        orient="auto-start-reverse"
                                    >
                                        <path d="M 0 0 L 10 5 L 0 10 z" fill={PALETTE} />
                                    </marker>
                                </defs>
                            </g>
                        </g>
                    </svg>
                )}
            </div>

            {tool === 'polygon' && drawingPoints.length > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="plan-drawing-hint">
                    {t('plan.draw_polygon_hint', 'Click to add points · double-click or Enter to finish · Esc to cancel')}
                </div>
            )}
            {tool === 'line' && drawingPoints.length > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="plan-drawing-hint">
                    {t('plan.draw_line_hint', 'Click the second point to finish · Esc to cancel')}
                </div>
            )}
        </div>
    )
}

interface DrawToolbarProps {
    tool: Tool
    onToolChange: (t: Tool) => void
    onCancel: () => void
}

function DrawToolbar({ tool, onToolChange, onCancel }: DrawToolbarProps) {
    const { t } = useTranslation('assets')
    const TOOLS: { id: Tool; icon: string; labelKey: string; defaultLabel: string }[] = [
        { id: 'select', icon: mdiCursorDefault, labelKey: 'plan.tool_select', defaultLabel: 'Select' },
        { id: 'polygon', icon: mdiVectorPolygon, labelKey: 'plan.tool_polygon', defaultLabel: 'Polygon' },
        { id: 'point', icon: mdiCircleOutline, labelKey: 'plan.tool_point', defaultLabel: 'Point' },
        { id: 'line', icon: mdiVectorLine, labelKey: 'plan.tool_line', defaultLabel: 'Line' },
    ]
    return (
        <div className="flex items-center gap-1" data-testid="plan-toolbar">
            {TOOLS.map(toolDef => {
                const label = t(toolDef.labelKey, toolDef.defaultLabel)
                return (
                    <Button
                        key={toolDef.id}
                        variant={tool === toolDef.id ? 'default' : 'outline'}
                        size="icon"
                        aria-label={label}
                        title={label}
                        onClick={() => {
                            if (tool === toolDef.id && toolDef.id !== 'select') {
                                onCancel()
                            } else {
                                onToolChange(toolDef.id)
                            }
                        }}
                        data-testid={`plan-tool-${toolDef.id}`}
                    >
                        <AppIcon path={toolDef.icon} size="sm" />
                    </Button>
                )
            })}
        </div>
    )
}
